import React, { useState, useEffect } from 'react';
import { Save, Plus, Trash2, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { staticPagesService } from '../../services/api/staticPagesService';

interface Row {
  sno: string;
  services: string;
  duration: string;
  responsible_officer: string;
  remarks: string;
  if_not_satisfied: string;
}

interface Category {
  name: string;
  rows: Row[];
}

interface CharterData {
  categories: Category[];
  note: string;
}

const emptyRow = (): Row => ({
  sno: '', services: '', duration: '', responsible_officer: '', remarks: '', if_not_satisfied: '',
});

function generateHtml(data: CharterData): string {
  let html = `<div class="col-md-12">
                <h6 class="incentive-main-title mb-3" style="font-size: 18px;">
                   Citizen Charter
                </h6>
                <div class="table-responsive incentive-ess-policy">
                    <table class="table table-bordered">
                        <thead>
                            <tr>
                                <th>S.No.</th>
                                <th>Services</th>
                                <th>Duration (Days)</th>
                                <th>Responsible Officer</th>
                                <th>Remarks</th>
                                <th>If not satisfied, the officer to be contacted</th>
                            </tr>
                        </thead>
                        <tbody>`;

  for (const cat of data.categories) {
    html += `\n                            <tr>
                                <td colspan="6"><div class="web-color fs15">${cat.name}</div></td>
                            </tr>`;
    for (const row of cat.rows) {
      html += `\n                            <tr>
                                <td>${row.sno}</td>
                                <td>${row.services}</td>
                                <td>${row.duration}</td>
                                <td>${row.responsible_officer}</td>
                                <td>${row.remarks}</td>
                                <td>${row.if_not_satisfied}</td>
                            </tr>`;
    }
  }

  html += `\n                        </tbody>
                    </table>
                </div>
            </div>`;

  if (data.note) {
    html += `\n            <div class="col-12 mt-3">
                <div class="text-center"><span class="font-weight-bold">Note:</span><span> ${data.note}</span></div>
            </div>`;
  }

  return html;
}

const CitizenCharterForm: React.FC = () => {
  const [pageId, setPageId] = useState<number | null>(null);
  const [categories, setCategories] = useState<Category[]>([{ name: 'General', rows: [emptyRow()] }]);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setFetching(true);
      const response = await staticPagesService.getAll();
      const page = (response.data || []).find((p: any) => p.slug === 'citizen_charter');
      if (page) {
        setPageId(page.id);
        if (page.metadata?.categories) {
          setCategories(page.metadata.categories);
          setNote(page.metadata.note || '');
        } else {
          // Parse from HTML if no metadata
          setCategories([{ name: 'General', rows: [emptyRow()] }]);
        }
      }
    } catch {
      toast.error('Failed to load citizen charter');
    } finally {
      setFetching(false);
    }
  };

  const updateCategory = (catIdx: number, field: string, value: string) => {
    setCategories((prev) => prev.map((c, i) => i === catIdx ? { ...c, [field]: value } : c));
  };

  const addCategory = () => {
    setCategories((prev) => [...prev, { name: '', rows: [emptyRow()] }]);
  };

  const removeCategory = (catIdx: number) => {
    if (categories.length <= 1) { toast.error('Need at least one category'); return; }
    setCategories((prev) => prev.filter((_, i) => i !== catIdx));
  };

  const updateRow = (catIdx: number, rowIdx: number, field: keyof Row, value: string) => {
    setCategories((prev) => prev.map((c, ci) =>
      ci === catIdx ? {
        ...c,
        rows: c.rows.map((r, ri) => ri === rowIdx ? { ...r, [field]: value } : r),
      } : c
    ));
  };

  const addRow = (catIdx: number) => {
    setCategories((prev) => prev.map((c, ci) =>
      ci === catIdx ? { ...c, rows: [...c.rows, emptyRow()] } : c
    ));
  };

  const removeRow = (catIdx: number, rowIdx: number) => {
    setCategories((prev) => prev.map((c, ci) =>
      ci === catIdx ? { ...c, rows: c.rows.filter((_, ri) => ri !== rowIdx) } : c
    ));
  };

  const handleSubmit = async () => {
    // Validate
    for (const cat of categories) {
      if (!cat.name.trim()) { toast.error('All categories need a name'); return; }
    }

    const data: CharterData = { categories, note };
    const content = generateHtml(data);

    try {
      setLoading(true);
      if (pageId) {
        await staticPagesService.update(pageId, {
          slug: 'citizen_charter',
          title: 'CITIZEN CHARTER',
          content,
          metadata: data,
          section: '',
          display_order: 3,
          is_active: true,
        });
      } else {
        await staticPagesService.create({
          slug: 'citizen_charter',
          title: 'CITIZEN CHARTER',
          content,
          metadata: data,
          section: '',
          display_order: 3,
          is_active: true,
        });
      }
      toast.success('Citizen charter saved successfully');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Save failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Citizen Charter</h1>
          <p className="mt-1 text-sm text-gray-600">Manage citizen charter services and responsibilities</p>
        </div>
        <button onClick={handleSubmit} disabled={loading}
          className="inline-flex items-center px-4 py-2.5 rounded-lg text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50">
          {loading ? (
            <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>Saving...</>
          ) : (
            <><Save size={18} className="mr-2" />Save Changes</>
          )}
        </button>
      </div>

      {/* Categories */}
      {categories.map((cat, catIdx) => (
        <div key={catIdx} className="bg-white shadow-sm border border-gray-200 rounded-lg">
          {/* Category Header */}
          <div className="flex items-center gap-3 p-4 border-b border-gray-200 bg-gray-50 rounded-t-lg">
            <input type="text" value={cat.name} onChange={(e) => updateCategory(catIdx, 'name', e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
              placeholder="Category name (e.g., General, Technical)" />
            <button onClick={() => removeCategory(catIdx)} className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Remove category">
              <Trash2 size={18} />
            </button>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-16">S.No</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Services</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase w-24">Duration</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Responsible Officer</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">If Not Satisfied</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cat.rows.map((row, rowIdx) => (
                  <tr key={rowIdx} className="hover:bg-gray-50">
                    <td className="px-2 py-1.5">
                      <input type="text" value={row.sno} onChange={(e) => updateRow(catIdx, rowIdx, 'sno', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={row.services} onChange={(e) => updateRow(catIdx, rowIdx, 'services', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={row.duration} onChange={(e) => updateRow(catIdx, rowIdx, 'duration', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={row.responsible_officer} onChange={(e) => updateRow(catIdx, rowIdx, 'responsible_officer', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={row.remarks} onChange={(e) => updateRow(catIdx, rowIdx, 'remarks', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" />
                    </td>
                    <td className="px-2 py-1.5">
                      <input type="text" value={row.if_not_satisfied} onChange={(e) => updateRow(catIdx, rowIdx, 'if_not_satisfied', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm" />
                    </td>
                    <td className="px-2 py-1.5">
                      <button onClick={() => removeRow(catIdx, rowIdx)} className="p-1 text-gray-400 hover:text-red-500">
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Add Row */}
          <div className="p-3 border-t border-gray-100">
            <button onClick={() => addRow(catIdx)}
              className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
              <Plus size={16} className="mr-1" /> Add Row
            </button>
          </div>
        </div>
      ))}

      {/* Add Category */}
      <button onClick={addCategory}
        className="inline-flex items-center px-4 py-2.5 border-2 border-dashed border-gray-300 rounded-lg text-sm text-gray-600 hover:border-blue-400 hover:text-blue-600 w-full justify-center">
        <Plus size={18} className="mr-2" /> Add Category
      </button>

      {/* Note */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Note (displayed at bottom)</label>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)}
          className="block w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          placeholder="e.g., In case of any Problems, Inform the concerned..." />
      </div>
    </div>
  );
};

export default CitizenCharterForm;
